import { Image, KeyEvent, Label, Panel, PanelProps, PanelStyles, Tree, TreeNode } from "@mcleod/components";
import { Keys, LogManager } from "@mcleod/core";
import { RowUserMenuFavorite } from "../models/ModelUserMenuFavorite";
import { MainMenu } from "./MainMenu";
import { MenuFavorites } from "./MenuFavorites";
import { MenuTreeNodeLeaf } from "./MenuTreeNodeLeaf";

const log = LogManager.getLogger("common/menu/PanelFavorites");

export class PanelFavorites extends Panel {
    treeFavoritesMenu: Tree<MenuTreeNodeLeaf>;
    private mainMenu: MainMenu;

    constructor(mainMenu: MainMenu, props?: Partial<PanelProps>) {
        super({...props});
        this.mainMenu = mainMenu;
        MenuFavorites.addFavoritesChangedCallback(() => this.populateFavorItemsTree());

        const panelTitle = new Panel({
            fillRow: true,
            borderBottomWidth: 1,
            borderBottomColor: "strokeSecondary",
            paddingRight: 8,
            paddingLeft: 8,
            components: [
                new Label({
                    paddingLeft: 0,
                    paddingRight: 0,
                    id: "labelFavorites",
                    text: "Favorites",
                    fontSize: "small",
                    fontBold: true,
                    color: "default.light",
                    fillRow: true,
                    rowBreak: false,
                }),
                new Image({
                    id: "imageFavorite",
                    stroke: "default",
                    themeKey: "menu.favoriteImage"
                })
            ]
        });
        this.add(panelTitle);

        this.treeFavoritesMenu = new Tree({
            fillRow: true,
            maxHeight: 136,
            borderWidth: 0,
            selectedNodeBackgroundColor: "primary.lightest",
            selectedNodeColor: (node) => node.color
        });

        this.treeFavoritesMenu.setClassIncluded(PanelStyles.slimScrollbar);
        this.populateFavorItemsTree();
        this.treeFavoritesMenu.adHocKeyHandler = (event: KeyEvent) => this.handleFavoritesMenuArrowKeys(event);
    }

    private populateFavorItemsTree() {
        const menuNodes: MenuTreeNodeLeaf[] = MenuFavorites.getFavorites()
            .filter(fav => fav.data != null)
            .map(fav => this.createFavoriteMenuItem(fav));

        log.debug("favorite menu items", menuNodes);
        this.populateUnfilteredOneLevelMenuTree(menuNodes);
    }

    private createFavoriteMenuItem(favorite: RowUserMenuFavorite) {
        const menuItem = new MenuTreeNodeLeaf(this.mainMenu, { menuItem: favorite.data });
        menuItem.setLabelProps({marginLeft: 4});
        return menuItem;
    }

    private populateUnfilteredOneLevelMenuTree(nodes: MenuTreeNodeLeaf[]) {
        const root = new TreeNode({ tree: this.treeFavoritesMenu });
        for (const node of nodes) {
            root.addChild(node);
        }
        this.treeFavoritesMenu.getRootNode().padding = 0;
        this.treeFavoritesMenu.getRootNode().setChildren(root.getChildren());
        this.treeFavoritesMenu.selectedNode = null;
        if (nodes.length === 0) {
            if (this.contains(this.treeFavoritesMenu))
                this.remove(this.treeFavoritesMenu);
        } else {
            if (!this.contains(this.treeFavoritesMenu))
                this.add(this.treeFavoritesMenu);
        }
    }

    private handleFavoritesMenuArrowKeys(event: KeyEvent) {
        if (event.isKey(Keys.ARROW_UP, {}) &&
            MenuFavorites.getFavoriteCount() > 0 &&
            this.treeFavoritesMenu.getRootNode().getChild(0) === this.treeFavoritesMenu.selectedNode) {
            this.mainMenu.selectFirstToolboxItem();
            event.preventDefault();
            event.stopPropagation();
        } else if (event.isKey(Keys.ARROW_DOWN, {}) &&
            MenuFavorites.getFavoriteCount() > 0 &&
            this.treeFavoritesMenu.getRootNode().getChild(MenuFavorites.getFavoriteCount() - 1) === this.treeFavoritesMenu.selectedNode) {
            this.mainMenu.focus();
            event.preventDefault();
            event.stopPropagation();
        }
    }

    selectFirstFavorite() {
        if (this.treeFavoritesMenu == null || this.treeFavoritesMenu.getRootNode().getChildCount() <= 0)
            return;
        this.treeFavoritesMenu.focus();
        if (this.treeFavoritesMenu.getRootNode().getChildCount() > 0)
            this.treeFavoritesMenu.selectedNode = this.treeFavoritesMenu.getRootNode().getChild(0) as MenuTreeNodeLeaf;
    }
}
